import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import PieChart from '@cloudscape-design/components/pie-chart';
import { ChartCard } from './ChartCard';

export type BiPieDatum = { title: string; value: number; color?: string };

type Props = {
  title: string;
  subtitle?: string;
  data: readonly BiPieDatum[];
  valueFormatter: (value: number) => string;
};

/**
 * Gráfico de pizza do BI (Cloudscape `PieChart` dentro de `ChartCard`), com o valor formatado
 * no popover.
 *
 * Origem: sem issue — commit a0887214d, 2026-06-09 00:59 +0200 (39-H, charts do sales
 * Recharts → Cloudscape).
 *
 * Contrato: `data` com `title`/`value`/`color?`; `valueFormatter` decide a unidade (quem passa
 * centavos passa `formatCurrencyCents`). `color` indefinido é omitido do datum e a paleta
 * padrão do Cloudscape preenche.
 *
 * Usado por: `dashboard-sales/RevenueByPlanChart.tsx` (web `apps/admin`). Mora em
 * `dashboard-finance/charts` por histórico; nenhum chart do finance o usa hoje.
 */
export function BiPieChart({ title, subtitle, data, valueFormatter }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const items = data.map((d) => (d.color !== undefined ? d : { title: d.title, value: d.value }));
  return (
    <ChartCard title={title} {...(subtitle !== undefined ? { subtitle } : {})}>
      <PieChart
        data={items}
        size="small"
        hideFilter
        ariaLabel={title}
        detailPopoverContent={(datum): { key: string; value: string }[] => [
          { key: datum.title, value: valueFormatter(datum.value) },
        ]}
        empty={
          <Box textAlign="center" color="inherit">
            {t('finance.empty')}
          </Box>
        }
      />
    </ChartCard>
  );
}
